// [목적] 폴리오 북마크 토글 훅 — 낙관적 업데이트 + 실패 롤백, react-query 캐시를 상태 저장소로 공유
// [이유] fe-folio-social-wire — useFolioLike 와 동일 패턴(ADR: 캐시 공유형 낙관적 상태).
//         [G2-1, 2026-09-17] 계약 C1(GET /folios/:id 응답 viewer.bookmarked) 반영 — initialBookmarked
//         파라미터 추가. BE 는 단건 상세에서만 viewer 를 채우므로(목록 응답엔 없음) 호출부가
//         folio.viewer?.bookmarked 를 넘기면 새로고침 후에도 정확한 상태로 시드된다.
// [사이드 이팩트] bookmark/unbookmark API(JwtAuthGuard+CsrfGuard) 호출. 실패 시 이전 캐시로 롤백.
//                 guest 차단은 본 훅 책임이 아님 — 호출부가 useAuthGate('folio.bookmark').guard(toggle) 로 감싸야 함.
// [수정 시 고려사항] initialBookmarked 미전달(목록 카드 등 viewer 없는 컨텍스트)시 기본값 false 유지(기존 동작 보존).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { bookmark, unbookmark } from '../api/folio-social.client.js';
import { queryKeys } from '../api/queryKeys.js';

interface BookmarkState {
  bookmarked: boolean;
  bookmarkCount: number;
}

export interface UseFolioBookmarkResult {
  bookmarked: boolean;
  bookmarkCount: number;
  toggle: () => void;
  isPending: boolean;
}

/**
 * [목적] 폴리오 북마크 상태 + 토글 mutation — 낙관적 업데이트/롤백
 * [이유] fe-folio-social-wire — 북마크 버튼 onClick 에서 사용. folioId 단위로 캐시 공유.
 * [사이드 이팩트] onMutate 에서 즉시 bookmarked/bookmarkCount 반전, onError 시 이전 값 복원, onSuccess 시 서버 값 정합.
 * [수정 시 고려사항] initialBookmarkCount 는 최초 마운트 시 1회만 캐시 시드로 사용됨(staleTime Infinity).
 *                   onMutate 는 cancelQueries 를 await 하지 않고 fire-and-forget 처리(useFollowToggle.ts·
 *                   useFolioLike.ts 와 동일 이유) — await 시 toggle() 직후 동기 캐시 검증이 불가능해진다.
 */
export function useFolioBookmark(
  folioId: string,
  initialBookmarkCount: number,
  initialBookmarked = false,
): UseFolioBookmarkResult {
  const qc = useQueryClient();
  const queryKey = queryKeys.folioSocial.bookmarkState(folioId);

  const { data } = useQuery<BookmarkState>({
    queryKey,
    queryFn: () => ({ bookmarked: initialBookmarked, bookmarkCount: initialBookmarkCount }),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 10,
  });

  const state: BookmarkState = data ?? {
    bookmarked: initialBookmarked,
    bookmarkCount: initialBookmarkCount,
  };

  const mutation = useMutation<BookmarkState, Error, boolean, { previous: BookmarkState }>({
    mutationFn: async (wasBookmarked) => {
      const res = wasBookmarked ? await unbookmark(folioId) : await bookmark(folioId);
      return { bookmarked: res.bookmarked, bookmarkCount: res.bookmarkCount };
    },
    onMutate: (wasBookmarked) => {
      void qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<BookmarkState>(queryKey) ?? state;
      qc.setQueryData<BookmarkState>(queryKey, {
        bookmarked: !wasBookmarked,
        bookmarkCount: Math.max(0, previous.bookmarkCount + (wasBookmarked ? -1 : 1)),
      });
      return { previous };
    },
    onError: (_err, _wasBookmarked, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
    onSuccess: (result) => {
      qc.setQueryData(queryKey, result);
    },
  });

  return {
    bookmarked: state.bookmarked,
    bookmarkCount: state.bookmarkCount,
    toggle: () => mutation.mutate(state.bookmarked),
    isPending: mutation.isPending,
  };
}
